import React from 'react';
import { Bike, Car, MapPin } from 'lucide-react';
import { useParkings, useSettings } from '../lib/usePublicData';

const DEFAULT_INTRO = "Kom met de fiets of te voet — gezond, milieuvriendelijk én sneller dan parkeren zoeken. Zotte Zomer ligt in het hart van Ronse, vlot bereikbaar zonder auto. Toch met de wagen? Geen probleem — Ronse voorziet gratis randparkings op wandelafstand van het festival. Hieronder vind je de zes parkings waar je je auto zonder zorgen kan kwijt.";

const Mobility = () => {
    const { data: parkings } = useParkings();
    const { data: settings } = useSettings();
    const intro = settings?.mobility_intro_text || DEFAULT_INTRO;

    return (
        <section
            id="mobiliteit"
            data-testid="mobility-section"
            className="relative py-16 sm:py-24 px-5 sm:px-8"
        >
            <div className="mx-auto max-w-7xl">
                <header className="text-center mb-10 sm:mb-14">
                    <h2
                        data-testid="mobility-title"
                        className="zz-outline-title font-display"
                        style={{ fontSize: 'clamp(2rem, 6vw, 4rem)' }}
                    >
                        Hoe geraak je er?
                    </h2>
                    <div
                        className="mt-5 flex items-center justify-center gap-3 text-[var(--green-dark)]"
                        aria-hidden="true"
                    >
                        <Bike size={22} />
                        <span className="text-xl">·</span>
                        <span className="font-display text-sm">te voet</span>
                        <span className="text-xl">·</span>
                        <Car size={22} />
                    </div>
                </header>

                <div className="bg-cream/85 border-2 border-[var(--green)]/40 rounded-2xl p-6 sm:p-8 mb-10 max-w-3xl mx-auto">
                    <p
                        data-testid="mobility-intro"
                        className="text-base sm:text-lg text-[var(--green-dark)] leading-relaxed"
                    >
                        {intro}
                    </p>
                </div>

                {parkings && parkings.length > 0 && (
                    <ul
                        data-testid="parkings-grid"
                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5"
                    >
                        {parkings.map((p) => {
                            const inner = (
                                <>
                                    <div className="flex items-start gap-2.5">
                                        <span
                                            className="shrink-0 mt-0.5 w-8 h-8 rounded-full bg-[var(--mint)] border-2 border-[var(--green-dark)] flex items-center justify-center"
                                            aria-hidden="true"
                                        >
                                            <MapPin size={14} className="text-[var(--green-dark)]" />
                                        </span>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="font-display text-sm sm:text-base text-[var(--green-dark)] leading-tight">
                                                {p.name}
                                            </h3>
                                            <p className="mt-1 text-xs sm:text-sm text-[var(--green-dark)]/85 break-words">
                                                {p.address}
                                            </p>
                                            {p.description && (
                                                <p className="mt-1 text-xs text-[var(--green-dark)]/70 italic">
                                                    {p.description}
                                                </p>
                                            )}
                                            {p.maps_url && (
                                                <span className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-[var(--green)] uppercase tracking-wider">
                                                    Routebeschrijving <span aria-hidden="true">→</span>
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <span
                                        className="absolute top-3 right-3 font-display text-[10px] tracking-wider uppercase px-2 py-0.5 rounded-full bg-[var(--yellow)] text-[var(--green-dark)] border border-[var(--green-dark)]/40"
                                        aria-hidden="true"
                                    >
                                        Gratis
                                    </span>
                                </>
                            );
                            const cls = "relative bg-white rounded-2xl p-4 sm:p-5 border-2 border-[var(--green)]/40 shadow-[0_3px_0_var(--green-dark)] transition-transform hover:-translate-y-1";
                            return (
                                <li key={p.id} data-testid={`parking-card-${p.id}`}>
                                    {p.maps_url ? (
                                        <a href={p.maps_url} target="_blank" rel="noopener noreferrer" className={`block ${cls}`}>{inner}</a>
                                    ) : (
                                        <div className={cls}>{inner}</div>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </section>
    );
};

export default Mobility;
